import { Heading } from "zudoku/components";
import { ChevronDown, Minus, Plus } from "zudoku/icons";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "zudoku/ui/Collapsible.js";
import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  Suspense,
  useState,
} from "react";

/*
  Stripe.js-style reference blocks for the MDX pages of a JS reference.

  Every parameter and every response field is written out here. Do not send the
  reader to a type described somewhere else.

  <Method id="add-item" signature="subbly.cart.addItem(payload)">
    Prose about the method.

    <Params title="Method parameters">     # every parameter, inline
      <Param name="payload" required type="object">
        Prose about the parameter.
        <Properties label="payload" collapsed>
          <Param name="quantity" optional type="number">…</Param>
          <ParamGroup title="Subscription products">…</ParamGroup>
        </Properties>
      </Param>
    </Params>

    <Returns type="Promise<Cart>">         # every response field, inline
      Prose about the result.
      <Properties>
        <Param name="id" type="string">…</Param>
      </Properties>
    </Returns>

    <Example>Code blocks, in the sticky right column.</Example>
  </Method>

  `id` is the anchor of the method on its group page; it comes from
  src/reference/<reference>/sections.ts.
*/

// Chrome (headings, badges) opts out of the page's prose styles with
// `not-prose`; descriptions stay inside the prose flow and are tightened
// by the `.js-ref-*` rules in styles.css.

export const Example = ({ children }: { children: ReactNode }) => (
  <div className="js-ref-example min-w-0">{children}</div>
);

const isExample = (child: ReactNode): child is ReactElement =>
  isValidElement(child) && child.type === Example;

export const Method = ({
  id,
  signature,
  title,
  children,
}: {
  id: string;
  /** Shown as the mono heading, e.g. `subblyCart.open(view?)`. */
  signature: ReactNode;
  /** When set, it is the heading and the signature becomes a chip below it. */
  title?: ReactNode;
  children: ReactNode;
}) => {
  const all = Children.toArray(children);
  const examples = all.filter(isExample);
  const body = all.filter((c) => !isExample(c));

  const chip = (
    /* A long signature has no space to break at, so let it break anywhere
       rather than push the page wider than the screen. `bg-secondary`, not
       `bg-muted`: muted is one step from the page background in the light
       theme, so the chip did not read as a chip. */
    <span className="inline-block rounded bg-secondary px-2 py-1 font-mono text-base font-semibold tracking-tight text-foreground [overflow-wrap:anywhere]">
      {signature}
    </span>
  );

  return (
    <section className="js-ref-method grid grid-cols-1 items-start gap-x-10 gap-y-6 py-4 lg:grid-cols-[minmax(0,4fr)_minmax(0,3fr)]">
      <div className="min-w-0 text-[0.9375rem] leading-relaxed">
        <div className="not-prose mb-5">
          <Heading
            level={2}
            id={id}
            registerNavigationAnchor
            className={
              title
                ? "js-ref-signature js-ref-title text-xl font-semibold tracking-tight scroll-mt-10 [&_a]:hidden"
                : "js-ref-signature text-base font-semibold scroll-mt-10 [&_a]:hidden"
            }
          >
            {title ?? chip}
          </Heading>
          {title && <div className="mt-2">{chip}</div>}
        </div>
        <div className="js-ref-body">{body}</div>
      </div>
      {examples.length > 0 && (
        <div className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-(--scroll-padding)">
          {examples}
        </div>
      )}
    </section>
  );
};

export const Params = ({
  title = "Method parameters",
  children,
}: {
  title?: string;
  children: ReactNode;
}) => (
  <div className="js-ref-params mt-8">
    <h3 className="not-prose mb-3 border-b border-border pb-2 text-lg font-semibold">
      {title}
    </h3>
    <div className="divide-y divide-border">{children}</div>
  </div>
);

/*
  Nested fields of an object parameter or of a response. Put it inside the
  <Param> or the <Returns> so the descriptions stay markdown. Open by default,
  like Stripe; pass `collapsed` for deep levels.

  <Param name="payload" type="object">
    Description.
    <Properties>
      <Param name="id" type="number">…</Param>
    </Properties>
  </Param>

  This component draws nothing itself. The parent reads `label` and `collapsed`
  off the element and draws the box, because only the parent knows the default
  label and where the box goes in the row.
*/
type PropertiesProps = {
  children: ReactNode;
  /** Word in the toggle: "Hide {label} properties". Read by the parent. */
  label?: string;
  /** Start the box closed. Read by the parent. */
  collapsed?: boolean;
  /** Render the fields only once the box is open; for generated trees that
      would otherwise put every branch of a cyclic schema in the page. */
  lazy?: boolean;
};

export const Properties = ({ children }: PropertiesProps) => <>{children}</>;

const isProperties = (child: ReactNode): child is ReactElement<PropertiesProps> =>
  isValidElement(child) && child.type === Properties;

/*
  Splits the children of a <Param> or a <Returns> into the one field list and
  the description around it.

  A second <Properties> is a mistake in the MDX: the parent draws one box, so
  the extra list disappears without a word. Stop the writer in development.
*/
const splitProperties = (children: ReactNode, block: string) => {
  const all = Children.toArray(children);
  const lists = all.filter(isProperties);
  if (lists.length > 1 && process.env.NODE_ENV !== "production") {
    throw new Error(
      `${block}: found ${lists.length} <Properties>; write one list.`,
    );
  }
  return { properties: lists[0], prose: all.filter((c) => !isProperties(c)) };
};

/*
  The box around a nested field list. <Param> and <Returns> both draw it, so
  the frame and the words of the toggle cannot drift apart.

  Radix drops the children of a closed Collapsible, and the site is
  prerendered, so a closed list would reach neither the static HTML, nor
  Pagefind, nor "Copy page". `forceMount` keeps the children in the tree; the
  `hidden` attribute, which is applied after the one Radix sets, takes the
  closed box off the screen.
*/
const PropertiesBox = ({
  label,
  collapsed,
  lazy,
  children,
}: {
  label: string;
  collapsed?: boolean;
  lazy?: boolean;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(!collapsed);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-3">
      <div className="rounded-md border border-border">
        <CollapsibleTrigger className="not-prose flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-muted-foreground hover:text-foreground">
          {open ? (
            <Minus size={14} aria-hidden="true" />
          ) : (
            <Plus size={14} aria-hidden="true" />
          )}
          {open ? `Hide ${label} properties` : `Show ${label} properties`}
        </CollapsibleTrigger>
        <CollapsibleContent forceMount={!lazy || undefined} hidden={!open}>
          {(open || !lazy) && (
            <div className="divide-y divide-border border-t border-border px-3">
              {/* A lazy list may suspend on its first open (the markdown
                  renderer waits for the highlighter); without a boundary
                  here the whole page would unmount and scroll to the top. */}
              <Suspense fallback={null}>{children}</Suspense>
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

/* The result of the method: prose, then the response fields in <Properties>. */
export const Returns = ({
  type,
  children,
}: {
  /** Shown next to the title, e.g. `Promise<Cart>`. */
  type?: string;
  children: ReactNode;
}) => {
  const { properties, prose } = splitProperties(children, "<Returns>");

  return (
    <div className="js-ref-returns mt-8">
      <h3 className="not-prose mb-3 flex flex-wrap items-baseline gap-x-2 border-b border-border pb-2 text-lg font-semibold">
        Returns
        {type && (
          <span className="font-mono text-xs font-normal text-muted-foreground">
            {type}
          </span>
        )}
      </h3>
      {prose.length > 0 && <div className="js-ref-body">{prose}</div>}
      {properties && (
        <PropertiesBox
          label={properties.props.label ?? "response"}
          collapsed={properties.props.collapsed}
          lazy={properties.props.lazy}
        >
          {properties.props.children}
        </PropertiesBox>
      )}
    </div>
  );
};

const Requirement = ({
  required,
  optional,
  conditional,
}: {
  required?: boolean;
  optional?: boolean;
  conditional?: boolean;
}) => {
  if (conditional) {
    return (
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">
        Conditionally required
      </span>
    );
  }
  if (required) {
    return (
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">
        Required
      </span>
    );
  }
  if (optional) {
    return <span className="text-xs text-muted-foreground">optional</span>;
  }
  return null;
};

const Deprecated = () => (
  <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground line-through">
    Deprecated
  </span>
);

export const Param = ({
  name,
  type,
  required,
  optional,
  conditional,
  deprecated,
  children,
}: {
  name: string;
  type?: string;
  required?: boolean;
  optional?: boolean;
  conditional?: boolean;
  deprecated?: boolean;
  children?: ReactNode;
}) => {
  const { properties, prose } = splitProperties(children, `<Param name="${name}">`);

  return (
    <div className="js-ref-param py-3 text-[0.9375rem] leading-relaxed">
      <div className="not-prose flex flex-wrap items-baseline gap-x-2">
        {/* `js-ref-name` carries the size and the weight of the chip in
            styles.css, out of reach of the prose rules. */}
        <code className="js-ref-name font-mono text-foreground">{name}</code>
        <Requirement
          required={required}
          optional={optional}
          conditional={conditional}
        />
        {type && (
          <span className="font-mono text-xs text-muted-foreground">{type}</span>
        )}
        {deprecated && <Deprecated />}
      </div>
      {prose.length > 0 && <div className="js-ref-body mt-1">{prose}</div>}
      {properties && (
        <PropertiesBox
          label={properties.props.label ?? name}
          collapsed={properties.props.collapsed}
          lazy={properties.props.lazy}
        >
          {properties.props.children}
        </PropertiesBox>
      )}
    </div>
  );
};

/* A group inside `properties`, e.g. "Subscription products" vs "One-time products". */
export const ParamGroup = ({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="py-2">
      <CollapsibleTrigger className="not-prose flex w-full items-center justify-between gap-2 py-1 text-left text-sm font-semibold">
        {title}
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      {/* Kept in the tree for the prerender, as in <PropertiesBox>. */}
      <CollapsibleContent forceMount hidden={!open}>
        <div className="divide-y divide-border">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};
